import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Loader2, Plus, Pin, PinOff, Trash2, Archive, ArchiveRestore, NotebookPen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_OPTIONS = [
  { value: "",        label: "Default", chip: "bg-muted"              },
  { value: "amber",   label: "Amber",   chip: "bg-amber-200"          },
  { value: "sky",     label: "Sky",     chip: "bg-sky-200"            },
  { value: "emerald", label: "Emerald", chip: "bg-emerald-200"        },
  { value: "rose",    label: "Rose",    chip: "bg-rose-200"           },
];

const colorBg = (c) => ({
  amber: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  sky: "bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-800",
  emerald: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  rose: "bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800",
}[c] || "bg-card border-border");

export default function OwnerNotesPanel() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, title, body, color, is_pinned}
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/notes`, { params: { include_archived: includeArchived } });
      setNotes(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        title: editing.title || "Untitled",
        body: editing.body || "",
        color: editing.color || null,
        is_pinned: !!editing.is_pinned,
      };
      if (editing.id) {
        await axios.put(`${API}/owner-console/notes/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/owner-console/notes`, payload);
      }
      toast.success(editing.id ? "Note updated" : "Note created");
      setEditing(null);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (n) => {
    try {
      await axios.put(`${API}/owner-console/notes/${n.id}`, { is_pinned: !n.is_pinned });
      fetchNotes();
    } catch (err) {
      toast.error("Pin failed");
    }
  };

  const toggleArchive = async (n) => {
    try {
      await axios.put(`${API}/owner-console/notes/${n.id}`, { is_archived: !n.is_archived });
      toast.success(n.is_archived ? "Restored" : "Archived");
      fetchNotes();
    } catch (err) {
      toast.error("Archive failed");
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Delete "${n.title}" permanently?`)) return;
    try {
      await axios.delete(`${API}/owner-console/notes/${n.id}`);
      toast.success("Note deleted");
      fetchNotes();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <NotebookPen className="h-5 w-5" />
            Owner Notes
          </h3>
          <p className="text-sm text-muted-foreground">
            Private to your account. Markdown supported.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncludeArchived(!includeArchived)}
            data-testid="toggle-archived"
          >
            {includeArchived ? "Hide" : "Show"} archived
          </Button>
          <Button
            size="sm"
            onClick={() => setEditing({ title: "", body: "", color: "", is_pinned: false })}
            data-testid="new-note-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New note
          </Button>
        </div>
      </div>

      {editing && (
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <Input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="Title"
              className="text-lg font-semibold border-0 px-0 focus-visible:ring-0"
              data-testid="note-title-input"
            />
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              placeholder="Markdown supported... # Header, **bold**, - list, etc."
              className="min-h-[180px] font-mono text-sm"
              data-testid="note-body-input"
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground mr-2">Color:</span>
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setEditing({ ...editing, color: c.value })}
                    className={`h-6 w-6 rounded-full border-2 transition ${c.chip} ${
                      editing.color === c.value ? "ring-2 ring-offset-2 ring-primary" : "border-transparent"
                    }`}
                    aria-label={c.label}
                  />
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing({ ...editing, is_pinned: !editing.is_pinned })}
                  className={editing.is_pinned ? "text-amber-600" : ""}
                >
                  {editing.is_pinned ? <Pin className="h-4 w-4 mr-1" /> : <PinOff className="h-4 w-4 mr-1" />}
                  {editing.is_pinned ? "Pinned" : "Pin"}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-note-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Save
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : notes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            No notes yet. Click "New note" above to start.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {notes.map((n) => (
            <Card key={n.id} className={`${colorBg(n.color)} ${n.is_archived ? "opacity-60" : ""}`} data-testid={`note-card-${n.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {n.is_pinned && <Pin className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
                    <CardTitle className="text-base font-semibold truncate">{n.title}</CardTitle>
                  </div>
                  {n.is_archived && <Badge variant="outline">Archived</Badge>}
                </div>
                <CardDescription className="text-xs">
                  Updated {new Date(n.updated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="text-sm whitespace-pre-wrap line-clamp-6 font-mono">
                  {n.body || <span className="text-muted-foreground italic">(empty)</span>}
                </div>
                <div className="flex items-center gap-1 mt-3 pt-2 border-t">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...n })} data-testid={`edit-note-${n.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePin(n)}>
                    {n.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleArchive(n)}>
                    {n.is_archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => handleDelete(n)} data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
